import { validate as isValidUuid, v4 as uuidV4 } from 'uuid';

import { AppConfigDetailDTO, ServiceTitanPortalV2DataProvider } from '_lib/api/apiTypes.shim';
import { isValidPhone10, NonEmptyDnisArray, parsePhone10 } from '_lib/utils/helpers';

import type { PhoneCallURLState, CallPopRouteState } from '../types/PhoneCall.types';

const toPhoneCallId = (maybePhoneCallId: string | null) =>
  maybePhoneCallId && isValidUuid(maybePhoneCallId) ? maybePhoneCallId : null;

export const initPhoneCallUrlState = (routeState: CallPopRouteState): PhoneCallURLState => {
  const searchParams = new URLSearchParams(routeState.locationSearch);
  return {
    callerId: routeState.callerId,
    companyDNIS: routeState.companyDNIS,
    companyId: searchParams.get('companyId') || undefined,
    aiCallId: searchParams.get('aiCallId') || undefined,
    telephonyId: searchParams.get('telephonyId') || undefined,
    durationStart: parseInt(searchParams.get('durationStart') || '') || Date.now(),
    isManualCall: searchParams.get('isManualCall') === 'true',
    phoneCallId: toPhoneCallId(searchParams.get('phoneCallId')) || uuidV4()
  };
};

export const toCallerIdState = (maybeValidCallerId: string | undefined) => {
  const parsedCallerId = parsePhone10(maybeValidCallerId);
  const isValid = isValidPhone10(parsedCallerId);
  return {
    callerId: isValid ? parsedCallerId : undefined, // only set the callerId if it is valid
    isValid: isValid
  };
};

/**
 * If the dnis from the URL exists in the provided array, return it, otherwise return the first
 * item of the array.
 */
export const getCallPopDnis = (companyDnisArray: NonEmptyDnisArray, companyDnisFromUrl?: string) =>
  companyDnisFromUrl && companyDnisArray.some((o) => o.phoneNumber === companyDnisFromUrl)
    ? companyDnisFromUrl
    : companyDnisArray[0].phoneNumber;

// Service Titan Config Defaults

// original EH URL:
// https://enterprise-hub-callcenter.servicetitan.com/callcenter/incomingCall?callerId=NNNNNNNNNN&forwardingNumber=NNNNNNNNNN

const serviceTitanEnterpriseUrl =
  'https://enterprise-hub-callcenter.servicetitan.com/modules/centralized-contact-center/callcenter/incomingCallWithLocationSearch';
const serviceTitanPortalUrl = 'https://go.servicetitan.com';

export type BuildIFrameURLParams = {
  callerId: string;
  companyDNIS: string;
  providerOptions: ServiceTitanPortalV2DataProvider['providerOptions'];
  serviceTitanConfig: AppConfigDetailDTO['serviceTitanConfig'];
};

export const buildIFrameURL = ({ callerId, companyDNIS, providerOptions, serviceTitanConfig }: BuildIFrameURLParams) =>
  providerOptions.isEnterprise
    ? `${serviceTitanConfig?.serviceTitanEnterpriseUrl || serviceTitanEnterpriseUrl}?callerId=${callerId}&forwardingNumber=${companyDNIS}`
    : `${serviceTitanConfig?.serviceTitanPortalUrl || serviceTitanPortalUrl}/PartnerPortal/CallHandle?callerID=${callerId}&forwardingNumber=${companyDNIS}&portal=${providerOptions.portalName}`;
